import { userLocalCache } from 'lib/reader';
import {
  addBlockActivityEntry,
  onScreen,
  uploadAllEntries,
  Block,
  Viewport
} from '.';

let Visibility;
if (process.browser) Visibility = require('visibilityjs');

// config
const TRACK_INTERVAL = 1; // get visible blocks
const REPORT_INTERVAL = 10; // write to browser storage
const UPLOAD_INTERVAL = 30; // send to server
const PERCENT_ON_SCREEN = 50; // percent of element that must be visible
const INACTIVE_TIMEOUT = 300; // 5 minutes, stop tracking if user is inactive
let lastActiveTime = Date.now(); // time when user was last active
let headerOffset = 0; // height of reader header
let currentVisibleBlockId = null; // store user position for bookmark
let blockTimes = {}; // store the current block times

// Visibility intervals
let tracker = null;
let reporter = null;
let uploader = null;

// update time when user was last active
const updateLastActiveTime = () => {
  lastActiveTime = Date.now();
};

// add entry to browser storage
const report = ({ courseId, chapterId, userId, timeZone }) => {
  if (Object.keys(blockTimes).length) {
    addBlockActivityEntry({ blockTimes, chapterId, courseId, userId });

    // clear blockTimes
    for (let blockId in blockTimes) {
      if (blockTimes.hasOwnProperty(blockId)) {
        blockTimes[blockId] = 0;
      }
    }
  }

  // save user position to bookmark data
  if (currentVisibleBlockId) {
    userLocalCache.setItem({
      key: `bookmarkData-${courseId}`,
      value: { chapterId, blockId: currentVisibleBlockId },
      userId
    });
    currentVisibleBlockId = null;
  }
};

// track how many seconds each block is viewed
const trackBlocks = ({ courseId, chapterId, userId, timeZone }) => {
  // see which blocks are in viewport
  const checkViewport = () => {
    const viewport = new Viewport(headerOffset);
    let firstVisibleBlockFound = false;
    for (let blockId in blocks) {
      if (blocks.hasOwnProperty(blockId)) {
        const blockOnScreen = onScreen({
          viewport: viewport,
          block: blocks[blockId],
          requiredPercentOnScreen: PERCENT_ON_SCREEN
        });
        if (blockOnScreen) {
          blockTimes[blockId] += TRACK_INTERVAL;
          if (!firstVisibleBlockFound) {
            firstVisibleBlockFound = true;
            currentVisibleBlockId = parseInt(blockId, 10);
          }
        }
      }
    }
  };

  // stop Visibility intervals
  const stopTimers = () => {
    Visibility.stop(tracker);
    Visibility.stop(reporter);
  };

  // start Visibility intervals
  const startTimers = () => {
    if (!started) {
      checkViewport();
      started = true;
    }

    // Visibility every will only run function if tab is active
    tracker = Visibility.every(TRACK_INTERVAL * 1000, () => {
      // ensure that user is not inactive
      if (lastActiveTime + INACTIVE_TIMEOUT * 1000 > Date.now()) {
        checkViewport();
      }
    });
    reporter = Visibility.every(REPORT_INTERVAL * 1000, () => {
      report({ courseId, chapterId, userId, timeZone });
    });
  };

  report({ courseId, chapterId, userId, timeZone });

  if (!Visibility.isSupported()) {
    return;
  }

  let started = false;
  let blocks = {};
  blockTimes = {};

  let blockElements = document.querySelectorAll('.reader-block');
  [].forEach.call(blockElements, (el) => {
    let block = new Block(el);
    blocks[block.id] = block;
    blockTimes[block.id] = 0;
  });

  // start tracking
  stopTimers();
  startTimers();
};

// init chapter tracking
const initTracking = ({ courseId, chapterId, userId, hOffset, timeZone }) => {
  headerOffset = hOffset || 0;
  // start uploader
  Visibility.stop(uploader);
  uploader = Visibility.every(UPLOAD_INTERVAL * 1000, uploadAllEntries);

  // upload entries to server before unload
  // NOTE: This has been disabled for now because it is unreliable.
  // All requests might not reach the server, but more importantly
  // the responses might not be handled correctly. This could mean
  // we upload some entries twice if they are not removed after
  // a successful upload.
  // window.removeEventListener('beforeunload', upload);
  // window.addEventListener('beforeunload', upload);

  // keep track of keydown, mousemove and scroll events
  // to ensure that user is not inactive
  document.addEventListener('keydown', updateLastActiveTime);
  document.addEventListener('mousemove', updateLastActiveTime);
  document.addEventListener('scroll', updateLastActiveTime);

  // start tracking
  trackBlocks({ courseId, chapterId, userId, timeZone });
};

const stopTracking = ({ courseId, chapterId, userId, timeZone }) => {
  report({ courseId, chapterId, userId, timeZone });

  Visibility.stop(tracker);
  Visibility.stop(reporter);
  Visibility.stop(uploader);
  document.removeEventListener('keydown', updateLastActiveTime);
  document.removeEventListener('mousemove', updateLastActiveTime);
  document.removeEventListener('scroll', updateLastActiveTime);
  // window.removeEventListener('beforeunload', upload);
};

export default { initTracking, stopTracking, trackBlocks };
